import { Ray } from '@babylonjs/core/Culling/ray';
import { Axis, Color4, Matrix, Plane, Quaternion, Vector3 } from '@babylonjs/core/Maths';
import { LinesMesh } from '@babylonjs/core/Meshes/linesMesh';
import { MeshBuilder } from '@babylonjs/core/Meshes/meshBuilder';
import { TransformNode } from '@babylonjs/core/Meshes/transformNode';
import { Scene } from '@babylonjs/core/scene';
import { DeepImmutableObject } from '@babylonjs/core/types';
import { eMateType } from '@models/classes/enums';
import { MateNode } from '@models/classes/mates/mate-node';
import { rotationToVectorToRef, toRadians } from './math-helper';
import { updateMatrixOfAncestorChain } from './scene-helper';

const ENABLE_DEBUG_VISUAL = false;

// function limitFPE(val: number) {
//     return Math.round((val + Number.EPSILON) * 100000000) / 100000000;
// }

// function roundQuaterion(q: Quaternion) {
//     return new Quaternion(limitFPE(q.x), limitFPE(q.y), limitFPE(q.z), limitFPE(q.w));
// }
export function mateControlPointToConnector(
    mate: Partial<MateNode> & Pick<MateNode, 'mateType'>,
    c1Mesh: TransformNode,
    controlPointMesh: TransformNode
) {
    let grandParentContainerWorldMatrixInv: DeepImmutableObject<Matrix>;
    if (controlPointMesh.parent) {
        grandParentContainerWorldMatrixInv = controlPointMesh.parent.computeWorldMatrix(true).clone().invert();
    } else {
        grandParentContainerWorldMatrixInv = Matrix.IdentityReadOnly;
    }

    const targetTransformMatrix = c1Mesh.computeWorldMatrix().clone();
    targetTransformMatrix.multiplyToRef(grandParentContainerWorldMatrixInv, targetTransformMatrix);
    const position = new Vector3();
    const direction = new Vector3();
    targetTransformMatrix.decompose(undefined, undefined, position);

    Vector3.TransformNormalFromFloatsToRef(-1.0, 0, 0, targetTransformMatrix, direction);

    if (!mate.flip) {
        direction.scaleInPlace(-1);
    }
    direction.normalize();

    return {
        position,
        direction,
    };
}

const flipMatrix = new Matrix();
Quaternion.RotationAxis(Axis.Y, toRadians(180)).toRotationMatrix(flipMatrix);

const debugViz = new Map<string, LinesMesh>();

export function mateConnectors(
    mate: Partial<MateNode> & Pick<MateNode, 'mateType'>,
    followerMesh: TransformNode,
    c1Mesh: TransformNode,
    c2Mesh: TransformNode
) {
    updateMatrixOfAncestorChain(followerMesh, c2Mesh);
    c1Mesh.computeWorldMatrix(true);
    let mateOffset = Vector3.Zero();
    let worldDelta = Vector3.Zero(); //the translation that c2 would need to make to get to it's new position

    if (mate.mateType == eMateType.ball) {
        /**
         * Ball Mate
         * -move c2 until it's in the same position as c1
         * In the future this could be made more intelligent by rotating a node to satisfy this mate and another ball or slider mate
         */
        worldDelta = c1Mesh.getAbsolutePosition().subtract(c2Mesh.getAbsolutePosition());
    } else {
        /**
         * Aligning direction vectors
         * ------------------------------------------------
         * Easiest way to think about this:
         * Connectors have a direction vector, and an angle of rotation around the direction vector.
         * The connector meshes are rotated by the renderer such that the positive X axis is pointing
         * in the direction of the direction vector.  And the angle vector will be pointing in the direction
         * of the y axis.
         *
         * We want the world transformation matrix of the second connector to match the world transform of the first connector (optionally flipped).
         * The math for calculating the world matrix of a connector can be written as:
         *      <local transform matrix of connector> * <local transform matrix of connector parent> * <world transform matrix of grandparent, or identity matrix>
         * We are trying to solve for the local transform matrix of the connector parent so the full world transform matches the transform of the first connector,
         * so we can rewrite the equation as:
         *      <local transform matrix of connector parent> = <INVERSE local transform matrix of connector> * <world transform of first connector> * <INVERSE world transform matrix of grandparent, or identity matrix>
         *
         * Then we apply the result matrix to the connector parent.
         */

        const t = new Vector3();
        const r = new Quaternion();
        const s = new Vector3();
        c1Mesh.computeWorldMatrix(true).decompose(s, r, t);
        if (mate.offsetAngle && mate.offsetAngle !== 0) {
            const angleOffsetQ = Quaternion.RotationAxis(Axis.X, (mate.offsetAngle * Math.PI) / 180);
            r.multiplyInPlace(angleOffsetQ);
        }
        const targetWorldTransformMatrix = Matrix.Compose(followerMesh.scaling, r, t);
        const currentWorldTransformMatrix = c2Mesh.computeWorldMatrix(true).clone();

        if (!mate.flip) {
            flipMatrix.multiplyToRef(targetWorldTransformMatrix, targetWorldTransformMatrix);
        }
        const c2LocalTransform = Matrix.Compose(c2Mesh.scaling, c2Mesh.rotationQuaternion!, c2Mesh.position);

        let grandParentContainerWorldMatrix: DeepImmutableObject<Matrix>;
        if (followerMesh.parent) {
            grandParentContainerWorldMatrix = followerMesh.parent.computeWorldMatrix(true).clone();
        } else {
            grandParentContainerWorldMatrix = Matrix.IdentityReadOnly;
        }
        const grandParentContainerWorldMatrixInv = grandParentContainerWorldMatrix.clone().invert();

        let calculatedTargetWorldTransform: Matrix | undefined;

        if (mate.mateType.equalsAny(eMateType.revolute, eMateType.cylindrical)) {
            /**
             * For these types of mates, the objective is to match the direction vectors but allow the angle to float freely.
             * What this means in practice is that we have to split the quaternion into a swing and a twist component.
             * The swing component will rotate the direction vector to point the right way, along a rotation axis equal to the cross product of the X axis and the direction vector,
             * and the twist component will rotate the mesh around the direction vector to match the desired angles. We then throw away the "new" twist angle and use the original twist angle.
             * For this to work it's necessary to ensure that the same rotation axis is used for both the swing rotation of the target and the swing rotation of the current, if the directions match.
             * Otherwise the twist angles won't be referring to the same thing.
             */
            const targetQ = Quaternion.FromRotationMatrix(targetWorldTransformMatrix.getRotationMatrix());
            const currentQ = Quaternion.FromRotationMatrix(currentWorldTransformMatrix.getRotationMatrix());
            const targetDirection = Vector3.TransformNormal(Axis.X, targetWorldTransformMatrix).normalize();
            const currentDirection = c2Mesh.right;

            // At direction vectors close to or equal to the X axis, the twist angle becomes heavily affected by floating point errors, so we avoid loops by not updating the rotation if the direction vectors are close to each other.
            if (currentDirection.add(targetDirection).length() > 0.0001) {
                const targetST = decomposeSwingTwist(targetQ, targetDirection.scale(-1));
                const currentST = decomposeSwingTwist(currentQ, currentDirection);
                const targetRotationNew = targetST.swing.multiply(currentST.twist);

                calculatedTargetWorldTransform = Matrix.Compose(followerMesh.scaling, targetRotationNew, t);
            }
        } else if (mate.mateType.equalsAny(eMateType.fastened, eMateType.planar, eMateType.slider)) {
            calculatedTargetWorldTransform = targetWorldTransformMatrix;
        }

        if (calculatedTargetWorldTransform) {
            const followerMeshTransformMatrix = c2LocalTransform.clone().invert();
            followerMeshTransformMatrix.multiplyToRef(calculatedTargetWorldTransform, followerMeshTransformMatrix);
            followerMeshTransformMatrix.multiplyToRef(grandParentContainerWorldMatrixInv, followerMeshTransformMatrix);

            const resultFollowerRotMatrix = followerMeshTransformMatrix.getRotationMatrix();

            if (ENABLE_DEBUG_VISUAL) {
                const targetForward = Vector3.TransformNormal(Axis.X, targetWorldTransformMatrix).normalize();
                const targetLeft = Vector3.TransformNormal(Axis.Z, targetWorldTransformMatrix).normalize();

                const c2CalcWorldTransformMatrix = c2LocalTransform
                    .multiply(resultFollowerRotMatrix)
                    .multiply(grandParentContainerWorldMatrix);
                const calcDirection = Vector3.TransformNormal(Axis.X, c2CalcWorldTransformMatrix).normalize();
                const calcAngle = Vector3.TransformNormal(Axis.Z, c2CalcWorldTransformMatrix).normalize();
                updateVisualization(
                    mate._dynamicId || '',
                    c2Mesh.getScene(),
                    c2Mesh.getAbsolutePosition(),
                    calcAngle,
                    calcDirection,
                    targetLeft,
                    targetForward
                );
            } else {
                followerMesh.rotationQuaternion = Quaternion.FromRotationMatrix(resultFollowerRotMatrix);
                updateMatrixOfAncestorChain(followerMesh, c2Mesh); //update world matrix after having just rotated follower mesh
            }
        }

        if (mate.mateType == eMateType.fastened || mate.mateType == eMateType.revolute) {
            //translate so the connectors line up
            worldDelta = c1Mesh.getAbsolutePosition().subtract(c2Mesh.getAbsolutePosition());
            mateOffset = new Vector3(mate.offsetX, mate.offsetY, mate.offsetZ);
        } else if (mate.mateType == eMateType.planar) {
            /**
             * Planar Mate
             * - define a plane at the position of c1, with a normal in the direction of c1
             * - make a ray from c2 in the direction of c2, and try to intersect the c1 plane
             * - if it doesn't intersect, flip it and try again
             * - the intersection of the ray and plane is the new position of c2 (not considering offset)
             */
            const c1PlaneNormal = Axis.X.rotateByQuaternionToRef(c1Mesh.absoluteRotationQuaternion, Vector3.Zero());
            const c1Plane = Plane.FromPositionAndNormal(c1Mesh.getAbsolutePosition(), c1PlaneNormal);
            const c2Direction = Axis.X.rotateByQuaternionToRef(c2Mesh.absoluteRotationQuaternion, Vector3.Zero());
            const ray = new Ray(c2Mesh.getAbsolutePosition(), c2Direction);
            let distance = ray.intersectsPlane(c1Plane);
            if (distance == null) {
                //if it doesn't intersect try flipping it
                ray.direction.negateInPlace();
                distance = ray.intersectsPlane(c1Plane);
            }
            worldDelta = ray.direction.scale(distance!);
            mateOffset = new Vector3(mate.offsetX, 0, 0); //only use the x component for planar mates
        } else if (mate.mateType == eMateType.slider || mate.mateType == eMateType.cylindrical) {
            /**
             * Slider Mate
             * - define a plane at the position of c2, with a normal in the direction of c2
             * - make a ray from c1 in the direction of c1, and try to intersect the c2 plane
             * - if it doesn't intersect, flip it and try again
             * - the intersection of the ray and plane is the new position of c2 (not considering offset)
             */
            const c1Pos = c1Mesh.getAbsolutePosition();
            const c2Pos = c2Mesh.getAbsolutePosition();
            const c2PlaneNormal = c2Mesh.getDirection(Axis.X).normalize();
            const c2Plane = Plane.FromPositionAndNormal(c2Pos, c2PlaneNormal);
            const c1Direction = c1Mesh.getDirection(Axis.X).normalize();
            const ray = new Ray(c1Pos, c1Direction);
            let distance = ray.intersectsPlane(c2Plane);
            if (distance == null) {
                //if it doesn't intersect try flipping it
                ray.direction.negateInPlace();
                distance = ray.intersectsPlane(c2Plane);
            }
            worldDelta = ray.direction.scale(distance!).add(c1Pos).subtract(c2Pos);
            mateOffset = new Vector3(0, mate.offsetY, mate.offsetZ); //don't use the x component for slider mates
        } else if (mate.mateType == eMateType.parallel) {
            //nothing to do for parallel
        }
    }

    /**
     * now we process the offset and set the position of c2 parent node
     */
    if (!mate.flip) mateOffset.negateInPlace();
    if (mate._swap) mateOffset.y = -mateOffset.y;
    const worldOffset = mateOffset.rotateByQuaternionToRef(c1Mesh.absoluteRotationQuaternion, Vector3.Zero());
    const totalOffset = worldOffset.add(worldDelta);
    const newAbsPos = followerMesh.getAbsolutePosition().add(totalOffset);
    followerMesh.setAbsolutePosition(newAbsPos);
    followerMesh.computeWorldMatrix(true);
}

function updateVisualization(
    id: string,
    scene: Scene,
    position: Vector3,
    calcLeft: Vector3,
    calcForward: Vector3,
    targetLeft: Vector3,
    targetForward: Vector3
) {
    const lines = [
        [position.add(calcLeft), position, position.add(calcForward)],
        [position.add(targetLeft.scale(-1)), position, position.add(targetForward.scale(-1))],
    ];
    let viz = debugViz.get(id);
    if (!viz) {
        viz = MeshBuilder.CreateLineSystem(
            id,
            {
                lines,
                updatable: true,
                colors: [
                    [new Color4(1, 0, 0, 1), new Color4(1, 1, 1, 0.5), new Color4(1, 1, 0, 1)],
                    [new Color4(0, 1, 0, 1), new Color4(1, 1, 1, 0.5), new Color4(1, 1, 1, 1)],
                ],
            },
            scene
        );
        debugViz.set(id, viz);
    } else {
        viz = MeshBuilder.CreateLineSystem(id, { lines, updatable: true, instance: viz }, scene);
    }
}

/**
   Decompose the rotation on to 2 parts.
   1. Twist - rotation around the "direction" vector
   2. Swing - rotation around axis that is perpendicular to "direction" vector
   The rotation can be composed back by 
   rotation = swing * twist

   has singularity in case of swing_rotation close to 180 degrees rotation.
   if the input quaternion is of non-unit length, the outputs are non-unit as well
   otherwise, outputs are both unit
*/
function decomposeSwingTwist(rotation: Quaternion, _direction: Vector3) {
    // Ensure the direction vector is normalized
    const direction = _direction.clone().normalize();
    const twist = new Quaternion();
    const swing = new Quaternion();

    // Alternate code with indeterminate swing axis
    // const ra = new Vector3(rotation.x, rotation.y, rotation.z);
    // const p = direction.scale(Vector3.Dot(ra, direction));
    // twist.set(p.x, p.y, p.z, rotation.w);
    // twist.normalize();
    // rotation.multiplyToRef(twist.conjugate(), swing);
    rotationToVectorToRef(direction, Axis.X, swing);
    swing.conjugate().multiplyToRef(rotation, twist);

    return { twist, swing };
}
